import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Conversation } from "../api";
import { Avatar, netInfo, previewOf } from "../cockpit-ui";

/** Une commande de navigation/action proposée par la palette. */
export interface PaletteCommand {
  id: string;
  label: string;
  icon: ReactNode;
  run: () => void;
}

type Row = { kind: "cmd"; cmd: PaletteCommand } | { kind: "conv"; conv: Conversation };

/**
 * Palette de commandes (⌘K, style Slack/Raycast) : overlay centré qui mêle des
 * **commandes de navigation** et un **saut vers une conversation**. Navigation
 * 100 % clavier (↑/↓, Entrée, Échap). Filtre sur le libellé des commandes et le
 * titre/réseau/aperçu des conversations.
 */
// Monté uniquement quand ouverte (cf. Shell : `{paletteOpen && <CommandPalette/>}`)
// → état toujours frais à l'ouverture, sans reset par effet (StrictMode-safe).
export function CommandPalette({
  onClose,
  commands,
  conversations,
  onOpenConversation,
}: {
  onClose: () => void;
  commands: PaletteCommand[];
  conversations: Conversation[];
  onOpenConversation: (c: Conversation) => void;
}) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Focus au montage (effet sans setState → autorisé).
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const needle = q.trim().toLowerCase();
  const cmdRows: Row[] = useMemo(
    () =>
      commands
        .filter((c) => !needle || c.label.toLowerCase().includes(needle))
        .map((cmd) => ({ kind: "cmd", cmd }) as Row),
    [commands, needle],
  );
  const convRows: Row[] = useMemo(
    () =>
      conversations
        .filter((c) => !c.isArchived)
        .filter(
          (c) =>
            !needle ||
            (c.title || "").toLowerCase().includes(needle) ||
            netInfo(c.network).label.toLowerCase().includes(needle) ||
            (c.lastMessageBody || "").toLowerCase().includes(needle),
        )
        .slice(0, 8)
        .map((conv) => ({ kind: "conv", conv }) as Row),
    [conversations, needle],
  );
  const rows = useMemo(() => [...cmdRows, ...convRows], [cmdRows, convRows]);
  // Sélection effective, clampée au rendu (la liste rétrécit quand on tape).
  const active = rows.length ? Math.min(sel, rows.length - 1) : 0;

  // Garde l'item sélectionné visible.
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-i="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  function activate(row: Row | undefined) {
    if (!row) return;
    if (row.kind === "cmd") row.cmd.run();
    else onOpenConversation(row.conv);
    onClose();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSel(rows.length ? (active + 1) % rows.length : 0);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSel(rows.length ? (active - 1 + rows.length) % rows.length : 0);
    } else if (e.key === "Enter") {
      e.preventDefault();
      activate(rows[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  }

  const firstConvIdx = cmdRows.length;

  return (
    <>
      <div className="palette-scrim" onClick={onClose} />
      <div className="palette" role="dialog" aria-modal="true" aria-label="Recherche & commandes">
        <div className="palette-search">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="10.5" cy="10.5" r="7" />
            <path d="m20 20-4.2-4.2" />
          </svg>
          <input
            ref={inputRef}
            placeholder="Rechercher partout, ou taper une commande…"
            aria-label="Rechercher partout"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
          />
        </div>
        <div className="palette-list" ref={listRef} role="listbox" aria-label="Résultats">
          {cmdRows.length > 0 && <div className="palette-section">Aller à</div>}
          {cmdRows.map((row, i) =>
            row.kind === "cmd" ? (
              <button
                key={row.cmd.id}
                data-i={i}
                type="button"
                role="option"
                aria-selected={active === i}
                className={`palette-item ${active === i ? "active" : ""}`}
                onMouseMove={() => setSel(i)}
                onClick={() => activate(row)}
              >
                <span className="palette-icon">{row.cmd.icon}</span>
                <span className="palette-label">{row.cmd.label}</span>
              </button>
            ) : null,
          )}
          {convRows.length > 0 && <div className="palette-section">Conversations</div>}
          {convRows.map((row, j) =>
            row.kind === "conv" ? (
              <button
                key={row.conv.id}
                data-i={firstConvIdx + j}
                type="button"
                role="option"
                aria-selected={active === firstConvIdx + j}
                className={`palette-item ${active === firstConvIdx + j ? "active" : ""}`}
                onMouseMove={() => setSel(firstConvIdx + j)}
                onClick={() => activate(row)}
              >
                <Avatar chat={row.conv} size={24} />
                <span className="palette-label">
                  {row.conv.title || netInfo(row.conv.network).label}
                </span>
                <span className="palette-hint">{previewOf(row.conv)}</span>
              </button>
            ) : null,
          )}
          {rows.length === 0 && <div className="palette-empty">Aucun résultat</div>}
        </div>
      </div>
    </>
  );
}
